import { Button, Grid } from "@mui/material";
import * as React from "react";
import {
    ProperyChangeType,
    ChannelCustomization as ChannelCustomizationObj,
    updateCustomization,
} from "../../ChannelCustomizer/customizationHelpers";
import ChannelCustomization from "./ChannelCustomization";
import styled from "styled-components";
import { GetChannelQuery_channel } from "../Overview/__generated__/GetChannelQuery";
import gql from "graphql-tag";
import { CopyAll } from "@mui/icons-material";
import { copyToClipboard } from "../../CopyLabel/CopyLabel";
import { THEOplayerView } from "../../THEOPlayer/THEOplayer";
import { ErrorCode, InterceptableRequest, Player } from "theoplayer";
import axios from "axios";
import { HESPlink } from "../../Landing/SignUpComplete";
import { ChannelStatusType } from "../../../__generated__/globalTypes";
import moment from "moment";

const PlayerWrapper = styled.div`
    padding: 5px 10px;
`;

const InfoWrapper = styled.div`
    margin: 8px 10px;
    padding: 10px;
    border-radius: 8px;
    font-size: 13px;
    background-color: ${(props) => props.theme.infoLight};
`;

const LicenseWrapper = styled.div`
    margin: 5px;
    margin-bottom: 16px;
    padding: 10px 20px;
    background-color: #fafafa;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
`;

interface Props {
    channel: GetChannelQuery_channel;
    token?: string;
    playoutId: string;
    isAlias: boolean;
    manifestUrl: string;
    loading: boolean;
    onSave: () => void;
    customization: ChannelCustomizationObj;
    customizationChanged: boolean;
    onChangeCustomization: (updated: ChannelCustomizationObj) => void;
}

export const GET_THEO_TOKEN = gql`
    query GetTHEOTokenQuery($organizationId: String!, $channelId: String!, $aliasId: String!) {
        token(organizationId: $organizationId, channelId: $channelId, aliasId: $aliasId)
    }
`;

async function resolvePlayoutSrc(playoutId: string, token?: string): Promise<string> {
    const manifest = await axios.get(`${process.env.REACT_APP_DISCOVERY_URL}/${playoutId}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const { hespSrc, hlsSrc } = manifest.data;
    if (!hespSrc && hlsSrc) {
        return `${hlsSrc}?v=${moment().unix()}`;
    }
    if (hlsSrc) {
        try {
            await axios.get(hespSrc, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch {
            return `${hlsSrc}?v=${moment().unix()}`;
        }
    }
    return `${hespSrc}?v=${moment().unix()}`;
}

function ChannelPlayoutCustomization({
    channel,
    playoutId,
    isAlias,
    manifestUrl,
    token,
    customization,
    onChangeCustomization,
    customizationChanged,
    loading,
    onSave,
}: Props) {
    const [src, setSrc] = React.useState<string>("");
    const [channelState, setChannelState] = React.useState<ChannelStatusType | undefined>(undefined);

    React.useEffect(() => {
        async function getManifestUrl() {
            setSrc(await resolvePlayoutSrc(playoutId, token));
        }
        if (channel.channelStatus !== channelState) {
            setChannelState(channel.channelStatus);
            getManifestUrl();
        }
    }, [channel.channelStatus, channelState, playoutId, token]);

    React.useEffect(() => {
        async function getManifestUrl() {
            console.log("getting manifest");
            setSrc(await resolvePlayoutSrc(playoutId, token));
        }
        getManifestUrl();
    }, [playoutId, token]);

    function onCustomizationChange(type: ProperyChangeType, key: string, value: string | boolean | number | object) {
        const updatedCustomization = updateCustomization({ customization, type, key, value });
        onChangeCustomization(updatedCustomization);
    }

    const onPlayer = React.useCallback(
        (player: Player): void => {
            player.autoplay = true;
            player.source = {
                sources: [
                    {
                        src,
                        type: src.includes("manifest.json")
                            ? "application/vnd.theo.hesp+json"
                            : "application/x-mpegURL",
                        useManagedMediaSource: true,
                        latencyConfiguration: {
                            minimumOffset: customization.targetLatency * 0.66,
                            targetOffset: customization.targetLatency,
                            maximumOffset: customization.targetLatency * 1.5,
                            forceSeekOffset: 50,
                        },
                        ...(channel.streamConfig.drm.enabled === true && {
                            contentProtection: {
                                widevine: {
                                    licenseAcquisitionURL: channel.streamConfig.drm.widevine?.licenseAcquisitionUrl,
                                },
                                playready: {
                                    licenseAcquisitionURL: channel.streamConfig.drm.playready?.licenseAcquisitionUrl,
                                },
                                fairplay: {
                                    certificateURL: channel.streamConfig.drm.fairplay?.certificateUrl,
                                    licenseAcquisitionURL: channel.streamConfig.drm.fairplay?.licenseAcquisitionUrl,
                                },
                            },
                        }),
                    },
                ],
            };

            const element = document.querySelector(".vjs-modal-dialog-content");
            if (element) {
                element.textContent = "Livestream not available yet";
            }

            player.addEventListener("error", function (e) {
                if (e.errorObject.code === ErrorCode.MEDIA_LOAD_ERROR) {
                    const element = document.querySelector(".vjs-modal-dialog-content");
                    if (element) {
                        element.textContent = "Livestream not available yet";
                    }
                }
            });

            const interceptor = function (request: InterceptableRequest) {
                if (
                    request.url.includes("manifest.json") ||
                    request.url.includes("hspc") ||
                    request.url.includes("hspi")
                ) {
                    request.redirect({
                        ...request,
                        headers: {
                            ...request.headers,
                            Authorization: `Bearer ${token}`,
                        },
                    });
                }
            };

            player.network.addRequestInterceptor(interceptor);
        },
        [src, token, customization.targetLatency, channel.streamConfig],
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
                <InfoWrapper>
                    THEOlive is now fully integrated with THEOplayer. Unlock the full power of the THEOplayer & THEOlive
                    combination by updating your implementation. A detailed guide can be found{" "}
                    <HESPlink
                        link="https://www.theoplayer.com/docs/theoplayer/how-to-guides/web/theolive/getting-started/"
                        openInNewTab
                    >
                        here
                    </HESPlink>
                    .
                </InfoWrapper>
                <PlayerWrapper>
                    {typeof token !== "undefined" && src !== "" && (
                        <THEOplayerView onPlayerCreated={onPlayer} license={channel.organization.license} />
                    )}
                    {/* <Hidden lgDown={true}>
                        <LinksWrapper>
                            <ChannelCustomizationUsefulLinks />
                        </LinksWrapper>
                    </Hidden> */}
                </PlayerWrapper>
            </Grid>
            <Grid item xs={12} lg={5}>
                <LicenseWrapper>
                    <div className="font-bold text-sm">Your THEOplayer license</div>
                    <div className="text-xs text-gray-500 mb-4">
                        This is your personalized THEOplayer license to use. Are you a THEOplayer customer and do you
                        have your own licenses? You can include one of those as well in your player implementation.
                    </div>
                    <div className="text-xs block break-all bg-white px-4 py-2 ">
                        <div>{channel.organization.license}</div>
                        <div className="mt-2 flex flex-row justify-center">
                            <Button
                                size="small"
                                color="secondary"
                                startIcon={<CopyAll />}
                                onClick={async () => await copyToClipboard(channel.organization.license)}
                            >
                                Copy
                            </Button>
                        </div>
                    </div>
                </LicenseWrapper>
                <ChannelCustomization
                    loading={loading}
                    onSaveClick={onSave}
                    customizationChanged={customizationChanged}
                    customization={customization}
                    onCustomizationChange={(
                        type: ProperyChangeType,
                        key: string,
                        value: string | boolean | number | object,
                    ) => onCustomizationChange(type, key, value)}
                />
            </Grid>
        </Grid>
    );
}

export default ChannelPlayoutCustomization;
